import { darken } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';

import { isDefined } from '../../../utils/variables';
import { ToTransientProps } from '../../utils/styled-helpers';
import { reset } from '../../utils/styled-reset';

type ToggleGroupOption<T> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type Props<T extends string | number> = {
  items: ToggleGroupOption<T>[];
  value?: T;
  defaultValue?: T;
  /**
   * Disables every item in the group. Individual items can also be disabled
   * via `items[].disabled`.
   */
  disabled?: boolean;
  /**
   * @default row
   */
  direction?: 'row' | 'column';
  onChange?: (value: T) => void;
};

export function ToggleGroup<T extends string | number>({
  disabled = false,
  direction = 'row',
  ...props
}: Props<T>) {
  const [value, setValue] = React.useState(props.value ?? props.defaultValue);
  const name = React.useId();

  const checkedValue = props.value ?? value;

  return (
    <Component $direction={direction} role="radiogroup">
      {props.items.map((item, index) => (
        <Label
          $checked={checkedValue === item.value}
          $direction={direction}
          $disabled={disabled || (item.disabled ?? false)}
          $position={index === 0 ? 'first' : index === props.items.length - 1 ? 'last' : 'middle'}
          key={item.value}
        >
          <input
            checked={checkedValue === item.value}
            disabled={disabled || item.disabled}
            name={name}
            type="radio"
            value={item.value}
            onChange={() => {
              if (!isDefined(props.value)) {
                setValue(item.value);
              }
              props.onChange?.(item.value);
            }}
          />
          {item.label}
        </Label>
      ))}
    </Component>
  );
}

type ComponentProps = ToTransientProps<Required<Pick<Props<string | number>, 'direction'>>>;

const Component = styled.div<ComponentProps>`
  display: inline-flex;
  flex-direction: ${(props) => props.$direction};
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.color.gray.light};
`;

type LabelProps = ToTransientProps<{
  checked: boolean;
  direction: 'row' | 'column';
  disabled: boolean;
  position: 'first' | 'middle' | 'last';
}>;

const Label = styled.label<LabelProps>`
  ${reset}

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(0.5em * 0.75) calc(1.3em * 0.75);
  font-size: 14px;
  cursor: pointer;
  background-color: ${(props) =>
    props.$checked ? props.theme.color.selected : props.theme.color.gray.lightest};

  ${(props) =>
    props.$position !== 'first' &&
    (props.$direction === 'row'
      ? css`
          border-left: 1px solid ${props.theme.color.gray.light};
        `
      : css`
          border-top: 1px solid ${props.theme.color.gray.light};
        `)}

  ${(props) =>
    !props.$disabled &&
    !props.$checked &&
    css`
      &:hover {
        background-color: ${darken(0.05, props.theme.color.gray.lightest)};
      }

      &:active {
        background-color: ${darken(0.1, props.theme.color.gray.lightest)};
      }
    `}

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}

  input {
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  }
`;

export { ToggleGroupOption, Component as ToggleGroupStyled };
